/**
 * The protected-route wrapper.
 *
 * Auth gating happens in `beforeLoad`, never in a `useEffect`
 * inside the screen. An effect-based redirect renders the protected component
 * first — a visible flash of somebody's pools before the router bounces them to
 * sign-in — and it runs after the route's loaders have already fired requests
 * that were always going to 401.
 *
 * Usage, on every route that needs a session:
 *
 *   export const Route = createFileRoute("/")({
 *     beforeLoad: requireSignedIn,
 *     component: HomeScreen,
 *   });
 */
import { redirect } from "@tanstack/react-router";

import { getAuthState, type typeSessionUser } from "./getAuthState";

export const SIGN_IN_PATH = "/sign-in";

/**
 * Where to send the fan back to once they finish signing in.
 *
 * Kept as a single href string rather than a path plus a serialized search
 * object: it round-trips through one query param, survives a copy-paste, and
 * `validateSearch` on the sign-in route only has to coerce one value.
 */
export function buildRedirectTarget(location: {
  href: string;
}): string | undefined {
  const { href } = location;
  // Bouncing back to sign-in after signing in is a loop. Drop it and let the
  // fan land on the home screen instead.
  if (href === SIGN_IN_PATH || href.startsWith(`${SIGN_IN_PATH}?`)) {
    return undefined;
  }
  return href;
}

export type typeSignedInRouteContext = { user: typeSessionUser };

/**
 * `beforeLoad` for any authenticated route. Returns the signed-in user so child
 * routes can read `context.user` without re-reading the cookie themselves.
 */
export function requireSignedIn({
  location,
}: {
  location: { href: string };
}): typeSignedInRouteContext {
  const auth = getAuthState();
  if (auth.status === "signedIn") {
    return { user: auth.user };
  }

  const redirectTo = buildRedirectTarget(location);
  // `redirect()` returns a `Response`, not an `Error`, and throwing it is
  // TanStack Router's own control-flow signal for "stop loading, go there
  // instead." `only-throw-error` is right about hand-written code and wrong
  // about this one library contract.
  // oxlint-disable-next-line typescript/only-throw-error
  throw redirect({
    to: SIGN_IN_PATH,
    // `step` is explicit rather than left to `validateSearch`'s default so the
    // URL a fan lands on is the URL they can re-share: /sign-in?step=email.
    search: {
      step: "email" as const,
      ...(redirectTo === undefined ? {} : { redirect: redirectTo }),
    },
  });
}
